import React, { FC, PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import { getLobData } from '../../lib/utils/utils';
import styles from './lobFooter.module.scss';
import { Link } from '@digital-pulse/react';
import ArrowUp from '../../public/images/arrowUp.svg';
import { useLobFooterTranslations } from '../../lib/utils/translationHelpers';
import { LOB_FOOTER_TEXTS } from '../../lib/constants/lobFooter';

interface Props {
  lob?: string;
}

const LobFooter: FC<PropsWithChildren<Props>> = (): ReactElement => {
  const [isMobile, setIsMobile] = useState(false);
  const lobData = getLobData();
  const PAGE_CONS = useLobFooterTranslations();

  useEffect(() => {
    // Check if screen is mobile size (425px or less)
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 425);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const BackToTop = () => {
    // Find main content and set focus
    const mainContent = document.getElementById('content') || document.querySelector('main');
    if (mainContent) {
        mainContent.focus();
        mainContent.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }
  };

  const { firstRowLinks, secondRowLinks, footerDataCopyRightText } = lobData;

  // Map translated labels onto links — keyed by LOB_FOOTER_TEXTS constants to stay in sync with lobConfig
  const lobFooterLabelMap: Record<string, string | undefined> = {
    [LOB_FOOTER_TEXTS.aboutCVSCaremark]: PAGE_CONS?.aboutCVSCaremark,
    [LOB_FOOTER_TEXTS.contact]: PAGE_CONS?.contact,
    [LOB_FOOTER_TEXTS.termsAndConditions]: PAGE_CONS?.termsAndConditions,
    [LOB_FOOTER_TEXTS.privacyPolicy]: PAGE_CONS?.privacyPolicy,
    [LOB_FOOTER_TEXTS.accessibility]: PAGE_CONS?.accessibility,
    [LOB_FOOTER_TEXTS.memberRights]: PAGE_CONS?.memberRights,
    [LOB_FOOTER_TEXTS.vulnerabilityDisclosure]: PAGE_CONS?.vulnerabilityDisclosure
  };

  const translatedFirstRowLinks = firstRowLinks?.map((item) => ({
    ...item,
    Label: lobFooterLabelMap[item.Label] ?? item.Label
  }));

  const translatedSecondRowLinks = secondRowLinks?.map((item) => ({
    ...item,
    Label: lobFooterLabelMap[item.Label] ?? item.Label
  }));

  // Copyright label map — keyed by the English string from lobConfig
  const copyrightLabelMap: Record<string, string | undefined> = {
    [LOB_FOOTER_TEXTS.caremarkCopyright]: PAGE_CONS?.caremarkCopyright,
    [LOB_FOOTER_TEXTS.specialtyCopyright]: PAGE_CONS?.specialtyCopyright
  };

  const translatedCopyRightText = copyrightLabelMap[footerDataCopyRightText] ?? footerDataCopyRightText;

  // Render Back to Top component
  const backToTopElement = (
    <div className={styles.backToTop}>
      <div className={styles.backToTopInner}>
        <ArrowUp aria-hidden="true" />
        <Link href="#content" aria-label={PAGE_CONS?.backToTopAriaLabel} onClick={BackToTop}>
          {PAGE_CONS?.backToTop}
        </Link>
      </div>
    </div>
  );
  
  // Render first row links
  const firstRowLinksElement = (
    <ul className={styles.firstRowNav}>
      {translatedFirstRowLinks?.map((item, index) => {
        return (
          <li key={`first-row-${index}`}>
            <Link href={item.URL} aria-label={item.Label}>
              {item.Label}
            </Link>
          </li>
        );
      })}
    </ul>
  );
  return (
    <footer className={styles.footerContainer} role="contentinfo">
      <nav aria-label="Footer navigation" className={styles.firstRow}>
        {isMobile ? (
          <>
            {backToTopElement}
            {firstRowLinksElement}
          </>
        ) : (
          <>
            {firstRowLinksElement}
            {backToTopElement}
          </>
        )}
      </nav>
      <div className={styles.secondRow}>
        {translatedSecondRowLinks?.length > 0 && (
          <ul className={styles.secondRowNav}>
            {translatedSecondRowLinks.map((item, index) => {
              return (
                <li key={`second-row-${index}`}>
                  <Link href={item.URL} aria-label={item.Label}>
                    {item.Label}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
        {translatedCopyRightText && (
          <div
            className={styles.copyRight}
            dangerouslySetInnerHTML={{ __html: translatedCopyRightText }}
          ></div>
        )}
      </div>
    </footer>
  );
};

export default LobFooter;
